/**
 * Tags an outgoing share link with a token so a resulting signup can be traced
 * back to the share that produced it (GH-1192).
 *
 * IMPORTANT: shareLink() is synchronous on purpose. The clipboard write has to
 * happen inside the click handler's user-gesture context — awaiting a network
 * call first makes Safari deny it. The token is minted here and reported to the
 * server afterwards, fire and forget.
 *
 * Logged-out visitors get the plain URL back and nothing is recorded.
 */
import { useAuthUser } from "@/composables/useAuthUser";

const TOKEN_LENGTH = 16;
const TOKEN_ALPHABET =
    "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";

function generateToken() {
    const bytes = new Uint8Array(TOKEN_LENGTH);
    crypto.getRandomValues(bytes);

    return Array.from(
        bytes,
        (byte) => TOKEN_ALPHABET[byte % TOKEN_ALPHABET.length],
    ).join("");
}

function withToken(url, token) {
    return `${url}${url.includes("?") ? "&" : "?"}r=${token}`;
}

export function useShareLink() {
    const user = useAuthUser();

    /**
     * @param {string} url        The URL about to be shared.
     * @param {object|null} shareable  { type: 'giftlist'|'item'|'user', uuid }
     * @param {string} channel    'copy_link' or 'share_sheet'.
     * @returns {string} The URL to share.
     */
    function shareLink(url, shareable, channel = "copy_link") {
        if (!user.value || !shareable?.type || !shareable?.uuid) {
            return url;
        }

        const token = generateToken();

        // Deliberately not awaited: a failed report must not cost the user
        // their share.
        axios
            .post(route("shares.store"), {
                token,
                type: shareable.type,
                uuid: shareable.uuid,
                channel,
            })
            .catch(() => {});

        return withToken(url, token);
    }

    return { shareLink };
}
